<template>
  <div
    v-if="targetLabel"
    class="dashboard-target-indicator"
    role="status"
    :data-testid="sectionTag"
  >
    <DecorativeIcon :icon="mdiMonitorDashboard" size="16" />
    <span class="body-regular-small">
      Login as merchant opens
      <strong :data-testid="`${sectionTag}-name`">{{ targetLabel }}</strong>
    </span>
    <WButton
      variant="link"
      size="small"
      label="Use default"
      :section-tag="`${sectionTag}-reset`"
      @click="uiSettingsStore.resetDashboardTarget()"
    />
  </div>
  <div
    v-else-if="deploymentsStore.droppedBranch"
    class="dashboard-target-indicator dashboard-target-indicator--dropped"
    role="alert"
    :data-testid="`${sectionTag}-dropped`"
  >
    <DecorativeIcon :icon="mdiAlert" size="16" />
    <span class="body-regular-small">
      <strong :data-testid="`${sectionTag}-dropped-branch`">{{ deploymentsStore.droppedBranch }}</strong>
      is no longer deployed (its branch was probably merged or deleted), so Login as merchant is back
      to Default.
    </span>
    <WButton
      variant="link"
      size="small"
      label="Dismiss"
      :section-tag="`${sectionTag}-dropped-dismiss`"
      @click="deploymentsStore.dismissDroppedBranch()"
    />
  </div>
</template>

<script setup lang="ts">
/**
 * Always-visible reminder that Login as merchant is going to a non-default dashboard, with a
 * one-click return to Default. Also drops a saved deployment that is no longer offered and says
 * so until dismissed, since a toast shown during page load is easily missed.
 *
 * @example
 * <DashboardTargetIndicator section-tag="dashboard-target-indicator" />
 */
import { computed, onMounted, watch } from 'vue'

import { useUiSettingsStore } from '@/stores/ui-settings.store'
import { useDashboardDeploymentsStore } from '@/stores/dashboard-deployments.store'

import WButton from '@wasabi/actions/WButton.vue'

import DecorativeIcon from '@/components/shared/DecorativeIcon.vue'

import { mdiAlert, mdiMonitorDashboard } from '@mdi/js'

interface Props {
  sectionTag?: string
}

withDefaults(defineProps<Props>(), {
  sectionTag: 'dashboard-target-indicator'
})

// ============ STORES ============
const uiSettingsStore = useUiSettingsStore()
const deploymentsStore = useDashboardDeploymentsStore()

// ============ COMPUTED ============
const targetLabel = computed(() => {
  const target = uiSettingsStore.activeDashboardTarget
  if (target.kind === 'deployment') return target.branch
  if (target.kind === 'local') return 'Local dev'
  return null
})

// ============ WATCHERS ============
// Picking a new target answers the dropped-deployment notice, so it should not linger.
watch(
  () => uiSettingsStore.activeDashboardTarget.kind,
  kind => {
    if (kind !== 'default') deploymentsStore.dismissDroppedBranch()
  }
)

// ============ LIFECYCLE ============
onMounted(() => {
  deploymentsStore.reconcileSavedTarget()
})
</script>

<style scoped>
.dashboard-target-indicator {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 16px;
  background-color: rgba(var(--v-theme-info), 0.08);
  border-bottom: 1px solid rgba(var(--v-theme-info), 0.24);
}

.dashboard-target-indicator--dropped {
  background-color: rgba(var(--v-theme-warning), 0.16);
  border-bottom-color: rgba(var(--v-theme-warning), 0.48);
}
</style>
